import { localConfigs } from 'locales/LocalConfigs';
import {
  getBaseLocale,
  getSupportedLocale,
  isValidLocale
} from 'locales/helpers';
import { BaseLocales, Locales } from 'locales/i18nLingui';
import {
  ContentFormat,
  ContentTypes,
  LibraryItem,
  LibraryItemSummary
} from 'services/singles';

export const SINGLE_TRACK_MUSIC_ROUTE_CUTOVER_EPOCH = 1775038400000;
export const ENABLE_VIDEO_ROUTE_PHASED_ROLLOUT_ENV =
  'REACT_APP_ENABLE_VIDEO_ROUTE_PHASED_ROLLOUT';

export type SingleTrackRouteFamily =
  | 'guided'
  | 'talks'
  | 'music'
  | 'fallback';

type SupportedSeoLocale =
  | BaseLocales.English
  | BaseLocales.Spanish
  | BaseLocales.French
  | BaseLocales.German
  | BaseLocales.BrazilianPortuguese
  | BaseLocales.Italian
  | BaseLocales.Dutch;

type RouteSegments = {
  guided: string;
  talks: string;
  music: string;
};

const SINGLE_TRACK_ROUTE_SEGMENTS: Record<SupportedSeoLocale, RouteSegments> = {
  [BaseLocales.English]: {
    guided: 'video-guided-meditations',
    talks: 'video-talks',
    music: 'video-music'
  },
  [BaseLocales.Spanish]: {
    guided: 'meditaciones-guiadas-por-video',
    talks: 'charlas-en-video',
    music: 'musica-de-video'
  },
  [BaseLocales.French]: {
    guided: 'meditations-guidees-en-video',
    talks: 'discussions-en-video',
    music: 'musique-video'
  },
  [BaseLocales.German]: {
    guided: 'gefuehrte-meditationen-als-video',
    talks: 'video-vortraege',
    music: 'video-musik'
  },
  [BaseLocales.BrazilianPortuguese]: {
    guided: 'meditacoes-guiadas-em-video',
    talks: 'conversas-em-video',
    music: 'musica-em-video'
  },
  [BaseLocales.Italian]: {
    guided: 'meditazioni-guidate-video',
    talks: 'discorsi-video',
    music: 'musica-video'
  },
  [BaseLocales.Dutch]: {
    guided: 'video-begeleide-meditaties',
    talks: 'video-gesprekken',
    music: 'video-muziek'
  }
};

type ResolveSingleTrackRouteFamilyParams = {
  contentType?: ContentTypes;
  contentFormat?: ContentFormat;
  languageCode?: string;
  approvedAtEpoch?: number;
  slug?: string;
};

export interface BuildSingleTrackPathOptions {
  locale?: string;
  username?: string;
  slug?: string;
  approvedAtEpoch?: number;
}

export type BuildSingleTrackPathResult = {
  routeFamily: SingleTrackRouteFamily;
  routeSegment: string;
  pathname: string;
  isFallback: boolean;
};

export const SINGLE_TRACK_VIDEO_GUIDED_LEGACY_SLUG_ALLOWLIST = new Set([
  '639hz-program-your-brain-for-change-while-sleeping',
  '111-hz-the-holy-frequency',
  'deep-focus-music-for-studying-concentration-and-work',
  '741-hz-solfeggio-throat-chakra-healing-frequency-theta-waves',
  'key-manifestation-frequencies-639hz-528hz-33hz-gamma',
  'archangel-zadiiel-and-the-violet-flame-of-transmutation',
  '5-dot-5-breaths-per-minute-extended-version',
  'progressive-muscle-relaxation-for-sleep',
  'how-do-you-see-the-world',
  'progressive-muscle-relaxation-to-reduce-stress-and-anxiety',
  'clouds-visualization-exercise',
  'dropping-anchor-exercise',
  '10-points-practice',
  'mini-slow-flow-if-you-re-stealing-yoga-you-need-yoga',
  'quit-smoking-mindfully',
  'i-deserve-happiness',
  'breath-for-transformation',
  'secrets-of-success-without-stress-number-10-invite-the-light',
  'healing-with-the-element-of-earth',
  'meditation-for-success-and-peak-performance',
  'morning-peace-abundance-and-miracles-meditation',
  'mother-of-all-things-lao-tzu',
  'yoga-nidra-for-sleep'
]);

export const isVideoRoutePhasedRolloutEnabled = () =>
  process.env[ENABLE_VIDEO_ROUTE_PHASED_ROLLOUT_ENV] === 'true';

export const isSingleTrackVideoRoute = (pathname: string) => {
  if (!pathname) {
    return false;
  }

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 3) {
    return false;
  }

  const routeSegment = segments[segments.length - 2];
  const allSeoSegments = Object.values(SINGLE_TRACK_ROUTE_SEGMENTS).flatMap(
    value => [value.guided, value.talks, value.music]
  );

  return allSeoSegments.includes(routeSegment);
};

const SUPPORTED_SEO_LOCALES = new Set<SupportedSeoLocale>([
  BaseLocales.English,
  BaseLocales.Spanish,
  BaseLocales.French,
  BaseLocales.German,
  BaseLocales.BrazilianPortuguese,
  BaseLocales.Italian,
  BaseLocales.Dutch
]);

const normalizeSeoLocale = (localeOrLanguage?: string): SupportedSeoLocale => {
  if (!localeOrLanguage) {
    return BaseLocales.English;
  }

  if (isValidLocale(localeOrLanguage)) {
    const locale =
      localeOrLanguage === 'br'
        ? BaseLocales.BrazilianPortuguese
        : (localeOrLanguage as Locales);
    const baseLocale = getBaseLocale(locale);
    return SUPPORTED_SEO_LOCALES.has(baseLocale as SupportedSeoLocale)
      ? (baseLocale as SupportedSeoLocale)
      : BaseLocales.English;
  }

  const supportedLocale = getSupportedLocale(localeOrLanguage);
  return SUPPORTED_SEO_LOCALES.has(supportedLocale as SupportedSeoLocale)
    ? (supportedLocale as SupportedSeoLocale)
    : BaseLocales.English;
};

export const getSingleTrackRouteSegments = (
  locale?: string
): RouteSegments => {
  return SINGLE_TRACK_ROUTE_SEGMENTS[normalizeSeoLocale(locale)];
};

export const getLegacySingleTrackRouteSegments = (locale?: string) => {
  const seoLocale = normalizeSeoLocale(locale);
  const config = localConfigs[seoLocale];

  return {
    guided: config.guidedMeditations
  };
};

export const getAllSingleTrackRouteSegments = () => {
  const segments = new Set<string>(['guided-meditations']);

  Object.values(SINGLE_TRACK_ROUTE_SEGMENTS).forEach(value => {
    segments.add(value.guided);
    segments.add(value.talks);
    segments.add(value.music);
  });

  Object.values(localConfigs).forEach(config => {
    segments.add(config.guidedMeditations);
  });

  return Array.from(segments);
};

export const getSingleTrackRoutePaths = (
  locale?: string
): {
  seo: string[];
  legacy: string[];
  all: string[];
} => {
  const seoSegments = getSingleTrackRouteSegments(locale);
  const legacySegments = getLegacySingleTrackRouteSegments(locale);

  const seo = [seoSegments.guided, seoSegments.talks, seoSegments.music];
  const legacy = [legacySegments.guided];

  return {
    seo,
    legacy,
    all: Array.from(new Set([...seo, ...legacy]))
  };
};

const resolveCurrentSingleTrackRouteFamily = ({
  contentType,
  languageCode,
  approvedAtEpoch
}: ResolveSingleTrackRouteFamilyParams): SingleTrackRouteFamily => {
  if (!contentType) {
    return 'fallback';
  }

  if (contentType === 'GUIDED') {
    return 'guided';
  }

  if (contentType === 'TALKS') {
    return 'talks';
  }

  if (contentType !== 'MUSIC') {
    return 'fallback';
  }

  const normalizedLanguage = languageCode?.toLowerCase();
  const isEnglishMusic =
    normalizedLanguage === BaseLocales.English || normalizedLanguage === 'm1';

  if (!isEnglishMusic) {
    return 'music';
  }

  if (approvedAtEpoch == null) {
    return 'fallback';
  }

  return approvedAtEpoch < SINGLE_TRACK_MUSIC_ROUTE_CUTOVER_EPOCH
    ? 'guided'
    : 'music';
};

export const resolveSingleTrackRouteFamily = ({
  contentType,
  contentFormat,
  languageCode,
  approvedAtEpoch,
  slug
}: ResolveSingleTrackRouteFamilyParams): SingleTrackRouteFamily => {
  if (!isVideoRoutePhasedRolloutEnabled()) {
    return resolveCurrentSingleTrackRouteFamily({
      contentType,
      languageCode,
      approvedAtEpoch
    });
  }

  const normalizedLanguage = languageCode?.toLowerCase();
  const isEnglishLanguage =
    normalizedLanguage === BaseLocales.English || normalizedLanguage === 'm1';
  const isAudioLikeContentFormat =
    contentFormat == null || (contentFormat as string) === 'AUDIO';
  const isEnglishMusicAudio =
    contentType === 'MUSIC' && isEnglishLanguage && isAudioLikeContentFormat;

  if (isEnglishMusicAudio) {
    return 'guided';
  }

  if (slug && SINGLE_TRACK_VIDEO_GUIDED_LEGACY_SLUG_ALLOWLIST.has(slug)) {
    return 'guided';
  }

  if (contentFormat !== 'VIDEO' || !contentType) {
    return 'fallback';
  }

  if (contentType === 'GUIDED') {
    return 'guided';
  }

  if (contentType === 'TALKS') {
    return 'talks';
  }

  if (contentType === 'MUSIC') {
    return 'music';
  }

  return 'fallback';
};

const buildLegacyGuidedPath = (
  locale: string | undefined,
  username: string,
  slug: string
) => {
  const seoLocale = normalizeSeoLocale(locale);
  const legacyRouteSegment = getLegacySingleTrackRouteSegments(seoLocale).guided;
  const localePrefix =
    seoLocale === BaseLocales.English
      ? ''
      : `/${localConfigs[seoLocale].home}`;

  return `${localePrefix}/${username}/${legacyRouteSegment}/${slug}`;
};

const getItemApprovedAtEpoch = (
  libraryItem: LibraryItem | LibraryItemSummary
) => {
  return 'approved_at' in libraryItem
    ? libraryItem.approved_at?.epoch
    : undefined;
};

export const buildSingleTrackPath = (
  libraryItem: LibraryItem | LibraryItemSummary,
  options: BuildSingleTrackPathOptions = {}
): BuildSingleTrackPathResult => {
  const locale = options.locale ?? libraryItem.lang?.iso_639_1;
  const username = options.username ?? libraryItem.publisher?.username;
  const slug = options.slug ?? libraryItem.slug;
  const contentType = libraryItem.content_type;
  const contentFormat = libraryItem.content_format;
  const languageCode = libraryItem.lang?.iso_639_1;
  const approvedAtEpoch =
    options.approvedAtEpoch ?? getItemApprovedAtEpoch(libraryItem);

  const fallbackPath =
    username && slug ? `/${username}/guided-meditations/${slug}` : '';

  if (!username || !slug) {
    return {
      routeFamily: 'fallback',
      routeSegment: 'guided-meditations',
      pathname: fallbackPath,
      isFallback: true
    };
  }

  const routeFamily = resolveSingleTrackRouteFamily({
    contentType,
    contentFormat,
    languageCode,
    approvedAtEpoch,
    slug
  });

  if (!isVideoRoutePhasedRolloutEnabled()) {
    if (routeFamily === 'fallback') {
      return {
        routeFamily: 'fallback',
        routeSegment: 'guided-meditations',
        pathname: fallbackPath,
        isFallback: true
      };
    }

    const seoLocale = normalizeSeoLocale(locale);
    const routeSegment = getSingleTrackRouteSegments(seoLocale)[routeFamily];
    const localePrefix =
      seoLocale === BaseLocales.English
        ? ''
        : `/${localConfigs[seoLocale].home}`;

    return {
      routeFamily,
      routeSegment,
      pathname: `${localePrefix}/${username}/${routeSegment}/${slug}`,
      isFallback: false
    };
  }

  if (routeFamily === 'fallback') {
    return {
      routeFamily: 'guided',
      routeSegment: getLegacySingleTrackRouteSegments(locale).guided,
      pathname: buildLegacyGuidedPath(locale, username, slug),
      isFallback: false
    };
  }

  const seoLocale = normalizeSeoLocale(locale);
  const routeSegment = getSingleTrackRouteSegments(seoLocale)[routeFamily];
  const localePrefix =
    seoLocale === BaseLocales.English
      ? ''
      : `/${localConfigs[seoLocale].home}`;

  return {
    routeFamily,
    routeSegment,
    pathname: `${localePrefix}/${username}/${routeSegment}/${slug}`,
    isFallback: false
  };
};
